// AddressBlock — a truncated crypto address with a copy button.
//
// Used by the (W6-stubbed) wallet-provisioning screen to display a watch-only,
// xpub-derived deposit address. NO signing key is ever shown or created — this
// is display-only. The middle of the address is elided; copy yields the full
// string.

import { useState } from "react";
import { CheckIcon, CopyIcon } from "lucide-react";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

function truncate(addr: string): string {
  if (addr.length <= 16) return addr;
  return `${addr.slice(0, 8)}…${addr.slice(-6)}`;
}

export function AddressBlock({
  address,
  className,
}: {
  address: string;
  className?: string;
}) {
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard?.writeText(address);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch {
      // Clipboard may be unavailable (e.g. insecure context); ignore silently.
    }
  }

  return (
    <span
      data-slot="address-block"
      className={cn("inline-flex items-center gap-1.5", className)}
    >
      <code className="font-mono text-xs text-nx-text-2 tabular-nums">
        {truncate(address)}
      </code>
      <Button
        type="button"
        variant="ghost"
        size="icon-xs"
        onClick={copy}
        aria-label={copied ? "Copied" : "Copy address"}
      >
        {copied ? <CheckIcon className="text-nx-credit" /> : <CopyIcon />}
      </Button>
    </span>
  );
}
